import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { DateTimeDisplay } from "../../components/date-time-display";
import { ErrorNotice, LoadingView } from "../../components/feedback";
import {
  INPUT_LIMITS,
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from "../../constants";
import { requestAdminApi } from "../../lib/api-client";
import { formatDiagnosticValue } from "../../lib/format";
import type { TicketDetail, TicketPriority, TicketStatus } from "../../types";

type TicketDetailScreenProps = {
  ticketId: string;
  onBack: () => void;
};

/**
 * 診断情報の表示名
 */
const DIAGNOSTIC_LABELS: Record<string, string> = {
  app_build_number: "アプリの更新番号",
  app_locale: "アプリの表示言語",
  app_version: "アプリのバージョン",
  client_sent_at: "送信日時",
  created_at: "受付日時",
  device_model: "端末の機種",
  device_time_zone: "端末の地域設定",
  ip_address: "接続元情報",
  os_name: "端末の種類",
  os_version: "端末のバージョン",
} as const;

/**
 * 選択肢の値から表示名を得る
 *
 * @param options - 選択肢
 * @param value - 検索する値
 * @returns 対応する表示名
 */
const findOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
) => {
  return options.find((option) => option.value === value)?.label ?? value;
};

/**
 * 診断情報の項目名を日本語へ変換する
 *
 * @param key - 診断情報のキー
 * @returns 表示名
 */
const formatDiagnosticLabel = (key: string) => {
  return DIAGNOSTIC_LABELS[key] ?? "その他の情報";
};

/**
 * 診断情報の日時項目か判定する
 *
 * @param key - 診断情報のキー
 * @param value - 診断情報の値
 * @returns 管理画面の日時表示対象である場合はtrue
 */
const isDiagnosticDateTime = (key: string, value: unknown): value is string => {
  return key === "client_sent_at" && typeof value === "string";
};

/**
 * 状態として扱える値か判定する
 *
 * @param value - 判定対象
 * @returns 状態の場合はtrue
 */
const isTicketStatus = (value: string): value is TicketStatus => {
  return TICKET_STATUS_OPTIONS.some((option) => option.value === value);
};

/**
 * 優先度として扱える値か判定する
 *
 * @param value - 判定対象
 * @returns 優先度の場合はtrue
 */
const isTicketPriority = (value: string): value is TicketPriority => {
  return TICKET_PRIORITY_OPTIONS.some((option) => option.value === value);
};

/**
 * 問い合わせ詳細画面
 *
 * @param props.ticketId - 問い合わせID
 * @param props.onBack - 一覧へ戻る処理
 * @returns 問い合わせ詳細画面
 */
export const TicketDetailScreen = ({ ticketId, onBack }: TicketDetailScreenProps) => {
  // 問い合わせ詳細を保持する
  const [detail, setDetail] = useState<TicketDetail | null>(null);

  // 選択中の状態を保持する
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus>("open");

  // 選択中の優先度を保持する
  const [selectedPriority, setSelectedPriority] = useState<TicketPriority>("normal");

  // 返信本文を保持する
  const [replyMessage, setReplyMessage] = useState("");

  // 同じ返信の通信再送で使用する冪等キーを保持する
  const [replyIdempotencyKey, setReplyIdempotencyKey] = useState(() => {
    return crypto.randomUUID();
  });

  // 読み込み中かを保持する
  const [isLoading, setIsLoading] = useState(true);

  // 問い合わせ更新中かを保持する
  const [isUpdating, setIsUpdating] = useState(false);

  // 返信送信中かを保持する
  const [isSending, setIsSending] = useState(false);

  // 利用者向けエラーを保持する
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 完了通知を保持する
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 問い合わせ詳細を読み込む
  const loadTicket = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await requestAdminApi<TicketDetail>(
        `/v1/admin/support/tickets/${encodeURIComponent(ticketId)}`,
      );
      setDetail(result);
      setSelectedStatus(result.ticket.status);
      setSelectedPriority(result.ticket.priority);
    } catch (error: unknown) {
      console.error("Failed to load support ticket", error);
      setErrorMessage("お問い合わせの詳細を読み込めませんでした。");
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  // 表示する問い合わせが変わったときに詳細を読み込む
  useEffect(() => {
    void loadTicket();
  }, [loadTicket]);

  const handleUpdateTicket = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (detail?.ticket.status === "resolved") {
      setErrorMessage("解決済みのお問い合わせは変更できません。");
      return;
    }

    setIsUpdating(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await requestAdminApi<unknown>(`/v1/admin/support/tickets/${encodeURIComponent(ticketId)}`, {
        method: "PATCH",
        body: {
          status: selectedStatus,
          priority: selectedPriority,
        },
      });
      setSuccessMessage("状態と優先度を更新しました。");
      await loadTicket();
    } catch (error: unknown) {
      console.error("Failed to update support ticket", error);
      setErrorMessage("お問い合わせを更新できませんでした。");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSendReply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = replyMessage.trim();

    if (detail?.ticket.status === "resolved") {
      setErrorMessage("解決済みのお問い合わせには返信できません。");
      return;
    }

    if (!trimmedMessage) {
      setErrorMessage("返信内容を入力してください。");
      return;
    }

    setIsSending(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await requestAdminApi<unknown>(
        `/v1/admin/support/tickets/${encodeURIComponent(ticketId)}/messages`,
        {
          method: "POST",
          body: { message: trimmedMessage },
          headers: { "Idempotency-Key": replyIdempotencyKey },
        },
      );
      setReplyMessage("");
      setReplyIdempotencyKey(crypto.randomUUID());
      setSuccessMessage("返信を送信しました。");
      await loadTicket();
    } catch (error: unknown) {
      console.error("Failed to send support reply", error);
      setErrorMessage("返信を送信できませんでした。内容を確認してもう一度お試しください。");
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading && !detail) {
    return <LoadingView label="お問い合わせの詳細を読み込んでいます" />;
  }

  if (!detail) {
    return (
      <div className="page-stack">
        {errorMessage ? (
          <ErrorNotice message={errorMessage} onRetry={() => void loadTicket()} />
        ) : null}
        <button className="button button--secondary align-start" type="button" onClick={onBack}>
          一覧へ戻る
        </button>
      </div>
    );
  }

  const { ticket, messages, diagnostics, user } = detail;
  const isResolved = ticket.status === "resolved";

  return (
    <div className="page-stack">
      <section className="detail-heading">
        <button className="back-button" type="button" onClick={onBack}>
          <span aria-hidden="true">←</span> お問い合わせ一覧
        </button>
        <div className="detail-heading__title">
          <div>
            <p className="eyebrow">管理番号 {ticket.ticket_id}</p>
            <h1>{ticket.title || "件名なし"}</h1>
          </div>
          <span className={`badge badge--${ticket.status}`}>
            {findOptionLabel(TICKET_STATUS_OPTIONS, ticket.status)}
          </span>
        </div>
        <p>
          {user.display_name || "名前未登録"} · {user.email || user.user_id}
        </p>
      </section>

      {errorMessage ? <ErrorNotice message={errorMessage} /> : null}
      {successMessage ? (
        <div className="notice notice--success" role="status">
          <span aria-hidden="true">✓</span>
          <p>{successMessage}</p>
        </div>
      ) : null}

      <div className="detail-layout">
        <div className="detail-main">
          <section className="panel conversation-panel" aria-labelledby="conversation-title">
            <div className="panel__header">
              <div>
                <p className="eyebrow">お問い合わせ内容</p>
                <h2 id="conversation-title">会話</h2>
              </div>
              <span>{messages.length}件</span>
            </div>
            <div className="conversation" aria-live="polite">
              {messages.map((message) => (
                <article
                  key={message.message_id}
                  className={`message ${message.author_type === "admin" ? "message--admin" : "message--user"}`}
                >
                  <header>
                    <strong>
                      {message.author_type === "admin"
                        ? "管理者"
                        : message.author_type === "system"
                          ? "システム"
                          : user.display_name || "ユーザー"}
                    </strong>
                    <DateTimeDisplay value={message.created_at} />
                  </header>
                  <p>{message.body}</p>
                </article>
              ))}
            </div>
            <form className="reply-form" onSubmit={handleSendReply}>
              {isResolved ? (
                <p className="resolved-note">解決済みのため、新しい返信は送信できません。</p>
              ) : null}
              <label htmlFor="reply-message">返信内容</label>
              <textarea
                id="reply-message"
                value={replyMessage}
                maxLength={INPUT_LIMITS.ticketReply}
                rows={6}
                placeholder="ユーザーへ送る内容を入力してください。"
                disabled={isResolved || isSending}
                onChange={(event) => {
                  setReplyMessage(event.target.value);
                  setReplyIdempotencyKey(crypto.randomUUID());
                }}
              />
              <div className="form-footer">
                <span>
                  {replyMessage.length.toLocaleString("ja-JP")} /{" "}
                  {INPUT_LIMITS.ticketReply.toLocaleString("ja-JP")}文字
                </span>
                <button
                  className="button button--primary"
                  type="submit"
                  disabled={isResolved || isSending || !replyMessage.trim()}
                >
                  {isSending ? "送信しています…" : "返信を送信"}
                </button>
              </div>
            </form>
          </section>
        </div>

        <aside className="detail-aside">
          <form className="panel settings-panel" onSubmit={handleUpdateTicket}>
            <div className="panel__header">
              <div>
                <p className="eyebrow">対応内容</p>
                <h2>対応設定</h2>
              </div>
            </div>
            <label>
              <span>状態</span>
              <select
                value={selectedStatus}
                disabled={isResolved}
                onChange={(event) => {
                  if (isTicketStatus(event.target.value)) {
                    setSelectedStatus(event.target.value);
                  }
                }}
              >
                {TICKET_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>優先度</span>
              <select
                value={selectedPriority}
                disabled={isResolved}
                onChange={(event) => {
                  if (isTicketPriority(event.target.value)) {
                    setSelectedPriority(event.target.value);
                  }
                }}
              >
                {TICKET_PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="metadata-list">
              <div>
                <span>分類</span>
                <strong>{findOptionLabel(TICKET_CATEGORY_OPTIONS, ticket.category)}</strong>
              </div>
              <div>
                <span>受付日時</span>
                <strong>
                  <DateTimeDisplay value={ticket.created_at} />
                </strong>
              </div>
            </div>
            {isResolved ? (
              <p className="resolved-note">解決済みのため、対応設定は変更できません。</p>
            ) : null}
            <button
              className="button button--primary button--full"
              type="submit"
              disabled={isResolved || isUpdating}
            >
              {isUpdating ? "更新しています…" : "変更を保存"}
            </button>
          </form>

          <details className="panel diagnostics-panel">
            <summary>
              <span>
                <strong>診断情報</strong>
                <small>調査に必要な場合のみ確認します</small>
              </span>
              <span aria-hidden="true">＋</span>
            </summary>
            <dl>
              {diagnostics && Object.keys(diagnostics).length > 0 ? (
                Object.entries(diagnostics).map(([key, value]) => (
                  <div key={key}>
                    <dt>{formatDiagnosticLabel(key)}</dt>
                    <dd>
                      {isDiagnosticDateTime(key, value) ? (
                        <DateTimeDisplay value={value} />
                      ) : (
                        formatDiagnosticValue(value)
                      )}
                    </dd>
                  </div>
                ))
              ) : (
                <div>
                  <dt>診断情報</dt>
                  <dd>送信されていません</dd>
                </div>
              )}
            </dl>
          </details>
        </aside>
      </div>
    </div>
  );
};
